import React from 'react';
import * as Blocks from './content-display-blocks';
import { ImageGallery, ProjectImageBlock } from '../reusable-ui-pieces/ImageGallery';


// Tech Stack sections are hidden on every project page.
// The content stays in the *-complete-content.js files — flip this to true to bring it back.
const SHOW_TECH_STACK = false;

const isTechStackSection = (section) =>
  section.id === 'stack' ||
  section.id === 'tech-stack' ||
  /stack|built with|was built/i.test(section.title || '');

// THE MAGIC PAGE BUILDER
export const AutomaticPageBuilder = ({ content }) => {
  // Renders any block based on its type
  const renderBlock = (block, index) => {
    if (!block || !block.type) return null;
    // Opt-out flag for individual blocks (kept in content, just not rendered)
    if (block.hidden) return null;

    // Unique key for each block
    const key = `${block.type}-${index}`;

    switch (block.type) {
      // 📝 TITLE BLOCK - Big headings (h1, h2, h3)
      case 'title':
        return <Blocks.TitleBlock key={key} {...block} />;
      
      // 📄 TEXT BLOCK - Regular paragraphs
      case 'text':
        return <Blocks.TextBlock key={key} {...block} />;
      
      // 🎥 VIDEO BLOCK - Embedded videos with captions
      case 'video':
        return <Blocks.VideoBlock key={key} {...block} />;
      
      // 🖼️ IMAGE BLOCK - Single image with caption
      case 'image':
        return <Blocks.ImageBlock key={key} {...block} />;
      
      // 🔀 SIDE BY SIDE BLOCK - Two items next to each other
      case 'sideBySide':
        return (
          <Blocks.SideBySideBlock 
            key={key}
            left={renderBlock(block.left, `${index}-left`)}
            right={renderBlock(block.right, `${index}-right`)}
            split={block.split}
            mobileStack={block.mobileStack}
          />
        );
      
      // 🖼️ GALLERY BLOCK - Grid of multiple images
      case 'gallery':
        return <Blocks.GalleryBlock key={key} {...block} />;
      
      // ⬜ SPACER BLOCK - Empty space between elements
      case 'spacer':
        return <Blocks.SpacerBlock key={key} {...block} />;
      
      // 🔘 BUTTON BLOCK - Call-to-action buttons
      case 'button':
        return <Blocks.ButtonBlock key={key} {...block} />;
      
      // 📦 CONTAINER BLOCK - Box with gray background
      case 'container':
        return (
          <Blocks.ContainerBlock key={key} {...block}>
            {block.blocks && block.blocks.map((innerBlock, i) => 
              renderBlock(innerBlock, `${index}-${i}`)
            )}
          </Blocks.ContainerBlock>
        );
    
      // 🎨 CUSTOM BLOCK - Your own HTML/React code
      case 'custom':
        return <div key={key}>{block.render()}</div>;
    
      // ADD these cases to your existing switch statement
      case 'project-gallery':
        return <ImageGallery key={key} {...block} />;
    
      case 'project-image':
        return <ProjectImageBlock key={key} {...block} />;
    
      default:
        console.warn(`Unknown block type: ${block.type}`);
        return null;
    }
  };

  // Render sections
  const renderSection = (section, sectionIndex) => {
    if (!section) return null;
    if (!SHOW_TECH_STACK && isTechStackSection(section)) return null;

    return (
      <section 
        key={`section-${sectionIndex}`}
        id={section.id}
        style={{
          marginBottom: section.spacing || 'clamp(48px, 8vw, 96px)',
          ...section.style
        }}
      >
        {/* Section Title (optional) */}
        {section.title && (
          <Blocks.TitleBlock 
            text={section.title} 
            size={section.titleSize || 'h2'}
            accent={section.titleAccent}
          />
        )}

        {/* Section Blocks */}
        {section.blocks && section.blocks.map((block, blockIndex) => 
          renderBlock(block, `${sectionIndex}-${blockIndex}`)
        )}
      </section>
    );
  };

  // Main render
  return (
    <div data-project-content style={{
      maxWidth: content.pageMaxWidth || '1200px',
      margin: '0 auto',
      padding: 'clamp(20px, 4vw, 40px)'
    }}>
      {/* Breadcrumb (optional) */}
      {content.breadcrumb && (
        <div style={{ marginBottom: 'clamp(32px, 5vw, 48px)' }}>
          <a href={content.breadcrumb.url} style={{ 
            color: 'var(--text-secondary)', 
            textDecoration: 'none',
            fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-accent)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}>
            ← {content.breadcrumb.text}
          </a>
        </div>
      )}

      {/* Render all sections */}
      {content.sections && content.sections.map((section, index) => 
        renderSection(section, index)
      )}

      {/* Call to Action: in the brand's own colours when the page gives them (callToAction.brand) */}
      {/* Closing card like Dribbble: the brand's own end card (one big link) + two clear buttons */}
      {content.callToAction && content.callToAction.endCard && (() => {
        const c = content.callToAction, btn = c.button || {}, url = btn.url || '/';
        const host = (() => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return ''; } })();
        const pill = { padding: '14px 26px', borderRadius: '999px', fontWeight: 600, fontSize: '15px', textDecoration: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'transform .2s ease, opacity .2s ease' };
        const lift = e => { e.currentTarget.style.transform = 'translateY(-2px)'; }, drop = e => { e.currentTarget.style.transform = 'none'; };
        return (
          <div style={{ marginTop: 'clamp(32px, 5vw, 56px)' }}>
            <a href={url} target="_blank" rel="noopener noreferrer" aria-label={host ? `Visit ${host}` : 'Visit'} style={{ display: 'block', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.28)', transition: 'transform .3s ease' }} onMouseEnter={lift} onMouseLeave={drop}>
              <img src={c.endCard} alt={c.title || ''} loading="lazy" style={{ width: '100%', display: 'block', aspectRatio: '8 / 3', objectFit: 'cover' }} />
            </a>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: 'clamp(20px, 3vw, 28px)' }}>
              <a href={url} target="_blank" rel="noopener noreferrer" onMouseEnter={lift} onMouseLeave={drop} style={{ ...pill, background: 'var(--text-primary, #fff)', color: 'var(--bg-primary, #0b0b0c)' }}>{host ? `Visit ${host}` : (btn.text || 'Visit')} ↗</a>
              <a href="/" onMouseEnter={lift} onMouseLeave={drop} style={{ ...pill, background: 'transparent', color: 'var(--text-primary, #fff)', border: '1px solid var(--border-color, rgba(255,255,255,0.25))' }}>See more work</a>
            </div>
          </div>
        );
      })()}
      {content.callToAction && content.callToAction.brand && !content.callToAction.endCard && (() => {
        const c = content.callToAction, b = c.brand, btn = c.button || {};
        return (
          <div style={{
            borderRadius: '28px', padding: 'clamp(44px, 8vw, 72px) 24px', textAlign: 'center',
            // a soft top light and a thin edge, so a near-black brand still reads on the dark theme
            // flat: when a logo carries its own background colour, the card must match it exactly
            background: b.flat ? b.bg : `radial-gradient(120% 85% at 50% 0%, rgba(255,255,255,0.10), transparent 60%), ${b.bg}`,
            border: '1px solid rgba(255,255,255,0.14)', color: b.fg,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10), 0 22px 50px rgba(0,0,0,0.28)', marginTop: 'clamp(24px, 4vw, 40px)'
          }}>
            {b.logo && <img src={b.logo} alt="" style={{ width: b.logoWidth || 72, height: 'auto', borderRadius: b.logoRadius || 0, marginBottom: '14px' }} />}
            {c.title && <div style={{
              fontSize: 'clamp(1.6rem, 4.4vw, 2.3rem)', fontWeight: b.titleWeight || 700, letterSpacing: b.titleSpacing || '-0.03em',
              fontStyle: b.titleStyle || 'normal', fontFamily: b.font || 'inherit', lineHeight: 1.1, textWrap: 'balance'
            }}>{c.title}</div>}
            {c.text && <div style={{ fontSize: '15px', opacity: 0.9, marginTop: '6px' }}>{c.text}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '24px' }}>
              {btn.url && (
                <a href={btn.url} target={/^https?:/.test(btn.url) ? '_blank' : undefined} rel="noopener noreferrer" style={{
                  padding: '12px 20px', borderRadius: '14px', background: b.btnBg, color: b.btnFg,
                  fontWeight: 700, fontSize: '14px', textDecoration: 'none'
                }}>{btn.text}</a>
              )}
              <a href="/" style={{
                padding: '12px 20px', borderRadius: '14px', background: 'rgba(255,255,255,0.1)', color: b.fg,
                fontWeight: 600, fontSize: '14px', textDecoration: 'none', border: `1px solid ${b.line || 'rgba(255,255,255,0.4)'}`
              }}>See more work ↗</a>
            </div>
          </div>
        );
      })()}
      {content.callToAction && !content.callToAction.brand && !content.callToAction.endCard && (
        <Blocks.ContainerBlock background={true} narrow={true}>
          <div style={{ textAlign: 'center' }}>
            {content.callToAction.title && (
              <Blocks.TitleBlock 
                text={content.callToAction.title} 
                size="h3" 
              />
            )}
            {content.callToAction.text && (
              <Blocks.TextBlock 
                content={content.callToAction.text} 
                align="center" 
              />
            )}
            {content.callToAction.button && (
              <Blocks.ButtonBlock
                {...content.callToAction.button}
                style={content.callToAction.button.style || 'primary'}
                size="large"
              />
            )}
          </div>
        </Blocks.ContainerBlock>
      )}
    </div>
  );
};

// Helper function to create sections easily
export const createSection = (title, blocks, options = {}) => ({
  title,
  blocks,
  ...options
});

// 🎨 PRESET LAYOUTS - Ready-made section templates
export const layouts = {
  // 🦸 HERO LAYOUT - Big title + subtitle + button
  // Creates: Welcome page with call-to-action
  hero: (title, subtitle, buttonText, buttonUrl) => ({
    blocks: [
      { type: 'title', text: title, size: 'h1', accent: true },
      { type: 'text', content: subtitle, size: 'large', align: 'center' },
      { type: 'spacer', size: 'small' },
      { type: 'button', text: buttonText, url: buttonUrl, style: 'primary', size: 'large' }
    ]
  }),

  // 📖 TEXT + IMAGE LAYOUT - Content with visual
  // Creates: Story section with accompanying image
  textWithImage: (text, imageSrc, imageOnRight = true) => ({
    blocks: [{
      type: 'sideBySide',
      split: '60/40',
      left: imageOnRight ? { type: 'text', content: text } : { type: 'image', src: imageSrc },
      right: imageOnRight ? { type: 'image', src: imageSrc } : { type: 'text', content: text }
    }]
  }),

  // 🎬 VIDEO SHOWCASE - Featured video section
  // Creates: Video with title above and description below
  videoShowcase: (title, videoUrl, description) => ({
    blocks: [
      { type: 'title', text: title, size: 'h3' },
      { type: 'video', url: videoUrl, maxWidth: '900px' },
      { type: 'text', content: description, align: 'center' }
    ]
  }),

  // 🖼️ FEATURE GALLERY - Grid of feature images
  // Creates: Visual showcase of features/benefits
  featureGallery: (title, features) => ({
    blocks: [
      { type: 'title', text: title, size: 'h2' },
      { type: 'gallery', images: features, columns: 3 }
    ]
  })
};